/**
 * Context + hook for per-form-field extraction metadata.
 *
 * The mapping layer writes metadata at dec-page review time; the Step
 * components read it via useExtractionMetadata(fieldPath) to render the
 * "from your policy" badge and source-quote tooltips.
 */

import { createContext, useContext } from "react";
import type { FieldMetadata, ExtractedFieldMetadata } from "@/types/extraction";

interface ExtractionMetadataContextValue {
  metadata: FieldMetadata;
  setMetadata: (updater: (prev: FieldMetadata) => FieldMetadata) => void;
}

export const ExtractionMetadataContext =
  createContext<ExtractionMetadataContextValue | null>(null);

export function useExtractionMetadata(
  fieldPath?: string,
): { metadata: FieldMetadata; fieldMetadata: ExtractedFieldMetadata | undefined; markEdited: (path: string) => void } {
  const ctx = useContext(ExtractionMetadataContext);
  const metadata = ctx?.metadata ?? {};
  return {
    metadata,
    fieldMetadata: fieldPath ? metadata[fieldPath] : undefined,
    markEdited: (path: string) => {
      ctx?.setMetadata((prev) => {
        const existing = prev[path];
        if (!existing) return prev;
        return {
          ...prev,
          [path]: {
            ...existing,
            source: "user_edited",
          },
        };
      });
    },
  };
}
